import type { DestroyOptions } from 'pixi.js';
import { Container, Rectangle, Point } from 'pixi.js';
import { Signal } from 'typed-signals';
import { TutorialTextBannerLayout } from '../../../../../layouts/tutorials/TutorialTextBannerLayout';
import { registerResizableObject } from '../../../../utils/pixi/registerResizableObject';
import { ColorOverlay } from '../../../views/common/ColorOverlay';
import { soundManager } from '../../../../app/SoundManager';

export interface TutorialTextOverlayOptions {
  readonly text: string;
  readonly overlay: Container;
  readonly onInComplete?: () => void;
  readonly onOutComplete?: () => void;
  readonly onClickAction?: () => void;
}

export class TutorialTextBannerView extends Container {
  private readonly colorOverlay = new ColorOverlay({
    fillColor: 'black',
    fillOpacity: 0.6,
    blockInteractivity: true,
    anchor: { x: 0.5, y: 0.5 },
  }).props({
    parent: this,
  });

  private readonly layout = new TutorialTextBannerLayout();

  private readonly onClickAction = new Signal<() => void>();

  private readonly onInComplete = new Signal<() => void>();

  private readonly onOutComplete = new Signal<() => void>();

  public static show(options: TutorialTextOverlayOptions): TutorialTextBannerView {
    const tutorialOverlay = new TutorialTextBannerView(options);
    options.overlay.addChild(tutorialOverlay);
    return tutorialOverlay;
  }

  private constructor(options: TutorialTextOverlayOptions) {
    super();

    const { root, bannerBodyText } = this.layout;

    this.addChild(root);

    bannerBodyText.text = options.text;

    if (options.onInComplete) this.onOutComplete.connect(options.onInComplete);
    if (options.onOutComplete) this.onOutComplete.connect(options.onOutComplete);
    if (options.onClickAction) this.onOutComplete.connect(options.onClickAction);

    this.animatePopIn(() => {
      this.onInComplete.emit();
      this.setClickToContinue();
    });

    registerResizableObject(this);
  }

  public destroy(options?: DestroyOptions): void {
    super.destroy(options);
    this.onInComplete.disconnectAll();
    this.onOutComplete.disconnectAll();
    this.onClickAction.disconnectAll();
  }

  private animatePopIn(callback: () => void, delay = 0): void {
    const { layout, colorOverlay } = this;
    const { tapToContinueText, bannerBodyText } = layout;

    soundManager.playDelayed('UI_Goal_Banner_Appear', 300);

    this.props({
      alpha: 0,
    });

    colorOverlay.fadeIn();

    this.tweenTo({ alpha: 1 }, 400, 'quadOut', true, delay);

    tapToContinueText.props({
      alpha: 0,
    });

    tapToContinueText.tweenTo({ alpha: 1 }, 400, 'sinOut', true, 1500).onComplete.addOnce(() => {
      tapToContinueText.tweenTo({ alpha: 0.4 }, 800, 'sinInOut', true, 100, -1, true);
    });

    bannerBodyText.props({
      scale: 1.2,
    });

    bannerBodyText.tweenTo({ scaleX: 1, scaleY: 1 }, 1200, 'bounceOut', true, delay).onComplete.addOnce(callback);
  }

  private animateToTopBar(topBarPos: Point, delay: number, onComplete: () => void): void {
    const { layout, colorOverlay } = this;
    const { banner, tapToContinueText } = layout;

    soundManager.playDelayed('UI_Goal_banner_FlyAway', delay + 250);

    const bannerMoveDuration = 400;
    const bannerFadeDuration = 300;

    tapToContinueText.removeTweens();
    tapToContinueText.tweenTo({ alpha: 0 }, 100, 'quadOut', true);

    banner.tweenTo(
      { alpha: 0 },
      bannerFadeDuration,
      'quadOut',
      true,
      delay + Math.max(bannerMoveDuration - bannerFadeDuration, 0),
    );

    banner.tweenTo({ y: topBarPos.y }, bannerMoveDuration, 'backIn', true, delay).onComplete.addOnce(onComplete);

    colorOverlay.fadeOut();
  }

  private setClickToContinue(): void {
    this.interactive = true;
    this.interactiveChildren = false;
    this.hitArea = new Rectangle(-20000, -20000, 40000, 40000);

    const onClick = (): void => {
      this.onpointertap = (): void => {};

      soundManager.play('UI_Button_Click');

      const foregroundLayout = this.layout.banner;
      const globalPos = new Point(0, 0);
      const localPos = foregroundLayout.children[0].parent?.toLocal(globalPos) ?? new Point();

      this.onClickAction.emit();

      this.animateToTopBar(localPos, 250, (): void => {
        this.parent?.removeChild(this);
        this.onOutComplete.emit();
        this.destroy();
      });
    };

    this.onpointertap = onClick;
  }

  public resize(width: number, height: number): void {
    this.x = width / 2;
    this.y = height / 2;
  }
}
